import { useQuery } from "@tanstack/react-query";
import { useAuth, usePermissions } from "@theg1group/rbac-ui";
import {
	AlertTriangle,
	BarChart3,
	BookOpenCheck,
	Bot,
	Building2,
	CheckCircle,
	Folder,
	HandCoins,
	Home,
	List,
	type LucideIcon,
	Settings as SettingsIcon,
	Sparkles,
	Tag,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { type MeResponse, api, getSelectedOrgId, setSelectedOrgId } from "../lib/api";
import { clearTokens } from "../lib/auth";

type NavItem = {
	to: string;
	label: string;
	Icon: LucideIcon;
	requires?: { resource: string; action: "view" | "create" | "edit" | "delete" };
};

type NavGroup = {
	title: string;
	items: NavItem[];
};

function OrgSwitcher() {
	const { data } = useQuery<MeResponse>({ queryKey: ["me"], queryFn: () => api.get("/me") });
	const orgs = data?.orgs ?? (data?.org ? [data.org] : []);
	const selected = getSelectedOrgId() ?? data?.org?.id ?? "";
	if (orgs.length === 0) return null;
	return (
		<div className="border-b border-neutral-200 px-3 py-2">
			<label
				htmlFor="org-switcher"
				className="block text-[10px] font-medium uppercase tracking-wider text-neutral-500"
			>
				Active org
			</label>
			<select
				id="org-switcher"
				value={selected}
				onChange={(e) => {
					setSelectedOrgId(e.target.value || null);
					location.reload();
				}}
				className="mt-1 w-full rounded border border-neutral-300 bg-white px-2 py-1 text-xs"
			>
				{orgs.map((o) => (
					<option key={o.id} value={o.id}>
						{o.displayName} ({o.kind})
					</option>
				))}
			</select>
		</div>
	);
}

const groups: NavGroup[] = [
	{
		title: "Marketplace",
		items: [
			{ to: "/", label: "Home", Icon: Home },
			{
				to: "/offers",
				label: "Offers",
				Icon: Tag,
				requires: { resource: "markets.offers", action: "view" },
			},
			{
				to: "/traffic",
				label: "Traffic",
				Icon: Folder,
				requires: { resource: "markets.traffic", action: "view" },
			},
			{
				to: "/negotiations",
				label: "Negotiations",
				Icon: Sparkles,
				requires: { resource: "markets.negotiations", action: "view" },
			},
			{
				to: "/deals",
				label: "Deals",
				Icon: BookOpenCheck,
				requires: { resource: "markets.deals", action: "view" },
			},
			{
				to: "/approvals",
				label: "Approvals",
				Icon: CheckCircle,
				requires: { resource: "markets.proposals", action: "view" },
			},
			{ to: "/agents", label: "Agents", Icon: Bot },
			{ to: "/directory", label: "Directory", Icon: List },
			{
				to: "/disputes",
				label: "Disputes",
				Icon: AlertTriangle,
				requires: { resource: "markets.disputes", action: "view" },
			},
			{
				to: "/reputation",
				label: "Reputation",
				Icon: BarChart3,
				requires: { resource: "markets.reputation", action: "view" },
			},
			{ to: "/billing", label: "Billing", Icon: HandCoins },
			{
				to: "/settings",
				label: "Settings",
				Icon: SettingsIcon,
				requires: { resource: "markets.settings", action: "view" },
			},
		],
	},
];

export function Layout({ children }: { children: React.ReactNode }) {
	const { user } = useAuth();
	const perm = usePermissions();
	const loc = useLocation();
	return (
		<div className="flex min-h-screen bg-neutral-50">
			<aside className="sticky top-0 flex h-screen w-60 flex-col border-r border-neutral-200 bg-white">
				<div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-3">
					<Building2 size={18} className="text-blue-600" />
					<span className="text-sm font-semibold text-neutral-900">g1.markets</span>
				</div>
				<OrgSwitcher />

				<nav className="flex-1 space-y-4 overflow-y-auto p-2">
					{groups.map((g) => {
						const visible = g.items.filter((item) => {
							if (!item.requires) return true;
							return perm.can(item.requires.resource, item.requires.action);
						});
						if (visible.length === 0) return null;
						return (
							<div key={g.title}>
								<div className="px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-neutral-500">
									{g.title}
								</div>
								{visible.map((item) => (
									<NavLink
										key={item.to}
										to={item.to}
										className={({ isActive }) =>
											`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
												isActive
													? "bg-blue-50 text-blue-700"
													: "text-neutral-700 hover:bg-neutral-100"
											}`
										}
									>
										<item.Icon size={14} className="flex-shrink-0" />
										<span>{item.label}</span>
									</NavLink>
								))}
							</div>
						);
					})}
				</nav>
				<div className="border-t border-neutral-200 p-3 text-xs">
					<div className="truncate font-medium text-neutral-900">
						{user?.email ?? user?.id?.slice(0, 8) ?? "—"}
					</div>
					<div className="text-neutral-500">role {user?.role ?? "?"}</div>
					<button
						type="button"
						onClick={() => {
							clearTokens();
							location.href = "/";
						}}
						className="mt-2 text-neutral-500 hover:text-neutral-900"
					>
						Sign out
					</button>
				</div>
			</aside>
			<main className="flex-1" key={loc.pathname}>
				{children}
			</main>
		</div>
	);
}
